import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, InputAdornment, Popover, TextField, Tooltip } from "@mui/material";
import { ArrowForwardRounded, SearchOutlined } from "@mui/icons-material";
import { isEmpty } from "lodash";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import RffInputWrapper from "../../../components/finalForm/shared/RffInputWrapper";
import { useMediaQueryHook } from "../../../hooks/useMediaQueryHook";

/**
 * LoanHeaderChipList
 *
 * Header list of loan entities (subject properties, guarantors, …) rendered as
 * chips with an overflow popover. Replaces the old comma-joined strings, which
 * turned into an unreadable horizontally scrolling strip on portfolio loans
 * (15 properties) and long guarantor lists.
 *
 * Rules, identical for every entity type:
 *   - At or under the threshold (2) every item renders as a chip — the common
 *     1–2 item case is unchanged apart from the chip styling.
 *   - Above it, the first N chips render plus a "+N more" chip that opens a
 *     lookup popover (filter input + scrollable list + link to the owning tab).
 *     Header height is therefore constant at any count.
 *   - Narrow viewports drop to a single visible chip rather than wrapping.
 *   - Clicking any chip — or any popover row — opens that entity's detail
 *     sidebar. The popover closes on selection.
 *
 * Consumers supply the data + accessors; this component owns the layout,
 * truncation, popover, and a11y wiring. See LoanHeaderProperties /
 * LoanHeaderGuarantors for the two configurations.
 */

// Max chips rendered inline before collapsing into "+N more".
const VISIBLE_CHIPS = 2;
const VISIBLE_CHIPS_NARROW = 1;

export const T = {
  ink: "#0f172a",
  ink3: "#64748b",
  line: "#e3e9ec",
  surface: "#f7f9fb",
  accent: "#167991",
  accentSoft: "#e6f1f4",
  accentLine: "#bcdae2",
};

// `maxWidth: "none"` is how the single-item case renders in full: with only one
// chip there's no competition for header space, so truncating it to an ellipsis
// hides information for no benefit.
export const Chip = ({ label, tooltip, icon, onClick, maxWidth = 220 }) => (
  <Tooltip title={tooltip || label} arrow placement="top" enterDelay={300}>
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        font: "inherit",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        maxWidth,
        px: "8px",
        py: "3px",
        borderRadius: "6px",
        border: `1px solid ${T.line}`,
        background: T.surface,
        color: T.ink,
        flexShrink: 0,
        cursor: "pointer",
        transition: "border-color 0.15s, background 0.15s",
        "&:hover": { borderColor: T.accentLine, background: T.accentSoft },
      }}
    >
      {icon}
      <Box
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Box>
    </Box>
  </Tooltip>
);

const LoanHeaderChipList = ({
  items,
  // Label for the wrapper — receives the item count.
  getLabel,
  // Chip text (truncated at the data level, not with CSS, so chips have
  // predictable widths). Receives `{ isOnly }` — the sole chip on a loan has
  // the room to render its full, untruncated label.
  getChipLabel,
  // Popover row: primary line, secondary line, right-aligned muted value.
  getPrimary,
  getSecondary,
  getTrailing,
  // Strings the filter input matches against.
  getSearchTerms,
  getKey = (item) => item?._id,
  getTooltip,
  chipIcon,
  onSelect,
  filterPlaceholder = "Filter",
  emptyLabel = "None",
  // Escape hatch — tab path appended to the drilldown base (e.g. "properties").
  viewAllPath,
  viewAllLabel,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSmallScreen, isLaptopSized } = useMediaQueryHook();
  const loanId = useSelector((state) => state.loanDrilldown?._id);

  const [anchorEl, setAnchorEl] = useState(null);
  const [filter, setFilter] = useState("");

  const list = useMemo(() => items || [], [items]);
  const count = list.length;
  const open = Boolean(anchorEl);

  // Narrow viewports show one chip so the header never wraps to a second row.
  const visibleLimit =
    isSmallScreen || isLaptopSized ? VISIBLE_CHIPS_NARROW : VISIBLE_CHIPS;

  // At or below the threshold everything renders inline — no "+N more" chip,
  // no popover. Only above it do we collapse.
  const visible = count <= VISIBLE_CHIPS ? list : list.slice(0, visibleLimit);
  const hiddenCount = count - visible.length;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) =>
      (getSearchTerms(item) || [])
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [list, filter, getSearchTerms]);

  const handleOpen = useCallback((e) => {
    setFilter("");
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSelect = useCallback(
    (item) => {
      setAnchorEl(null);
      onSelect?.(item);
    },
    [onSelect],
  );

  // Same base derivation the drilldown uses for its tab links, so the escape
  // hatch works when the screen is embedded under another route (e.g. the lock
  // queue's master-detail) and not just at /loan/:id.
  const handleViewAll = useCallback(() => {
    if (!loanId || !viewAllPath) return;
    const marker = `/${loanId}`;
    const idx = location.pathname.indexOf(marker);
    const base =
      idx === -1
        ? `/loan/${loanId}`
        : location.pathname.slice(0, idx + marker.length);
    setAnchorEl(null);
    navigate(`${base}/${viewAllPath}`);
  }, [loanId, viewAllPath, location.pathname, navigate]);

  if (isEmpty(list)) {
    return (
      <RffInputWrapper label={getLabel(0)} suppressGrid fullWidth={false}>
        <Txt sx={{ fontSize: 12, fontWeight: 600 }}>{emptyLabel}</Txt>
      </RffInputWrapper>
    );
  }

  return (
    <RffInputWrapper label={getLabel(count)} suppressGrid fullWidth={false}>
      <Flx ac g={0.75} sx={{ minWidth: 0 }}>
        {visible.map((item) => (
          <Chip
            key={getKey(item)}
            icon={chipIcon}
            // A lone chip renders its full label, uncapped — see Chip above.
            label={getChipLabel(item, { isOnly: count === 1 })}
            maxWidth={count === 1 ? "none" : 220}
            tooltip={getTooltip?.(item)}
            onClick={() => handleSelect(item)}
          />
        ))}

        {hiddenCount > 0 && (
          <Box
            component="button"
            type="button"
            onClick={handleOpen}
            aria-haspopup="dialog"
            aria-expanded={open}
            sx={{
              font: "inherit",
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              px: "8px",
              py: "3px",
              borderRadius: "6px",
              border: `1px solid ${T.accentLine}`,
              background: T.accentSoft,
              color: T.accent,
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: "nowrap",
              cursor: "pointer",
              "&:hover": { background: "#d6e8ed" },
            }}
          >
            +{hiddenCount} more
          </Box>
        )}
      </Flx>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            // A lookup surface, not a mini tab: filter + rows only. Anything
            // richer belongs in the owning tab, which already has the room.
            role: "dialog",
            "aria-label": getLabel(count),
            sx: {
              mt: 0.5,
              width: 340,
              borderRadius: "10px",
              border: `1px solid ${T.line}`,
              boxShadow: "0 8px 28px rgba(15,23,42,0.14)",
            },
          },
        }}
      >
        <Box sx={{ p: 1.25, borderBottom: `1px solid ${T.line}` }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={filterPlaceholder}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 16, color: T.ink3 }} />
                </InputAdornment>
              ),
              sx: { fontSize: 12, borderRadius: "8px" },
            }}
          />
        </Box>

        <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <Txt
              sx={{
                display: "block",
                p: 2,
                fontSize: 12,
                color: T.ink3,
                textAlign: "center",
              }}
            >
              No matches for “{filter.trim()}”
            </Txt>
          ) : (
            filtered.map((item) => (
              <Flx
                key={getKey(item)}
                component="button"
                type="button"
                ac
                jb
                g={1}
                onClick={() => handleSelect(item)}
                sx={{
                  font: "inherit",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  borderBottom: "1px solid #f1f5f9",
                  background: "transparent",
                  px: 1.5,
                  py: 1,
                  cursor: "pointer",
                  "&:last-of-type": { borderBottom: "none" },
                  "&:hover": { background: T.surface },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: T.ink,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getPrimary(item)}
                  </Txt>
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 11,
                      color: T.ink3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getSecondary(item) || "—"}
                  </Txt>
                </Box>
                <Txt
                  sx={{
                    fontSize: 11,
                    color: T.ink3,
                    flexShrink: 0,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {getTrailing?.(item) || ""}
                </Txt>
              </Flx>
            ))
          )}
        </Box>

        {viewAllPath && (
          <Box sx={{ borderTop: `1px solid ${T.line}`, p: 0.75 }}>
            <Box
              component="button"
              type="button"
              onClick={handleViewAll}
              sx={{
                font: "inherit",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                border: "none",
                background: "transparent",
                color: T.accent,
                fontSize: 12,
                fontWeight: 600,
                py: "6px",
                borderRadius: "6px",
                cursor: "pointer",
                "&:hover": { background: T.accentSoft },
              }}
            >
              {viewAllLabel}
              <ArrowForwardRounded sx={{ fontSize: 14 }} />
            </Box>
          </Box>
        )}
      </Popover>
    </RffInputWrapper>
  );
};

export default LoanHeaderChipList;
